import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { newsTranslations } from '../data/translations/newsTranslations';
import NewsletterSignup from '../components/NewsletterSignup';

export default function News() {
  const { language } = useLanguage();
  const t = newsTranslations[language].news;
  const navigate = useNavigate();

  if (!t || !t.posts || t.posts.length < 3) return null;

  const visiblePosts = t.posts.slice(0, 3);

  const handlePostClick = (post) => {
    if (post.internal) navigate(`/news/${post.id}`);
    else window.open(post.link, '_blank');
  };

  return (
    <div className="bg-background text-on-surface font-body relative overflow-hidden">
      {/* Background ambient light */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-96 bg-secondary/10 blur-[120px] pointer-events-none rounded-full" />

      <section className="py-24 px-4 sm:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex flex-col md:flex-row items-baseline justify-between mb-16 border-b border-border pb-6">
            <h2 className="font-headline text-4xl md:text-5xl font-extrabold text-primary uppercase tracking-tight">
              {t.title}
              <span className="block w-24 h-1 bg-secondary mt-4" />
            </h2>
            <Link
              to="/news"
              className="text-xs font-bold text-secondary flex items-center gap-2 uppercase tracking-[0.2em] hover:text-primary transition-colors mt-6 md:mt-0"
            >
              {t.readArchive} <span className="material-symbols-outlined text-sm">north_east</span>
            </Link>
          </div>

          {/* Horizontal accordion */}
          <div className="flex flex-col h-[500px] gap-2">
            {visiblePosts.map((post) => (
              <article
                key={post.id}
                onClick={() => handlePostClick(post)}
                className="relative overflow-hidden rounded-2xl border border-border bg-surface cursor-pointer group flex flex-row flex-1 hover:flex-[3] transition-all duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] shadow-sm hover:shadow-xl hover:border-secondary/30"
              >
                {/* Image — fixed width, full height */}
                <div className="relative w-72 flex-shrink-0 overflow-hidden">
                  {post.image ? (
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-secondary/30 to-primary/10" />
                  )}
                  {/* Fade edge into content area */}
                  <div className="absolute inset-y-0 right-0 w-10 bg-gradient-to-r from-transparent to-surface" />
                  {/* Play badge for video */}
                  {post.videoUrl && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-11 h-11 rounded-full bg-secondary flex items-center justify-center opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300 shadow-lg">
                        <span className="material-symbols-outlined text-primary text-2xl ml-0.5">play_arrow</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex flex-col justify-center px-6 py-5 flex-1 min-w-0">
                  {/* Meta */}
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-3 py-1 bg-secondary/10 text-secondary font-black text-[10px] uppercase tracking-[0.15em] rounded-full">
                      {post.source}
                    </span>
                    <span className="text-muted font-mono text-xs">{post.date}</span>
                  </div>

                  {/* Title — always visible */}
                  <h3 className="font-headline font-extrabold text-primary text-base md:text-lg leading-snug line-clamp-2">
                    {post.title}
                  </h3>

                  {/* Summary + CTA — revealed on expand */}
                  <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-700 ease-[cubic-bezier(0.25,1,0.5,1)]">
                    <div className="overflow-hidden">
                      <p className="text-on-surface opacity-0 group-hover:opacity-50 text-sm leading-relaxed mt-3 mb-4 line-clamp-3 transition-opacity duration-500 delay-150">
                        {post.summary}
                      </p>
                      <div className="flex items-center gap-2 text-secondary font-bold text-xs uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200">
                        {post.readMore}
                        <span className="material-symbols-outlined text-sm group-hover:translate-x-1 transition-transform duration-300">east</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 h-0.5 bg-secondary w-0 group-hover:w-full transition-all duration-1000 ease-in-out" />
              </article>
            ))}
          </div>

          {/* Subscription Section */}
          <div className="mt-20 sm:mt-28 w-full flex justify-center pb-10 max-w-5xl mx-auto">
            <NewsletterSignup />
          </div>

        </div>
      </section>
    </div>
  );
}
